// Getting GPX/TCX (or zipped) files in: straight into a Library folder, or onto the map unsaved (Save files them
// later), and saving a new route or track into a folder.
import { apiFetch } from '@/api/client';
import { addDraft } from './drafts';
import type { TrailRef } from './openTrails';
import type { ApiTrail, TrailKind } from './trail';

/** What the import accepts (the file picker's accept list). */
export const IMPORT_ACCEPT = '.gpx,.tcx,.zip';

export const isImportable = (file: File) => /\.(gpx|tcx|zip)$/i.test(file.name);

/** True while the drag holds files (not Library rows or text). */
export const dragHasFiles = (event: React.DragEvent) =>
  Array.from(event.dataTransfer.types).includes('Files');

export interface SavedItem {
  guid: string;
  kind: TrailKind;
  name: string;
}

function upload(file: File): FormData {
  const form = new FormData();
  form.append('file', file, file.name);
  return form;
}

async function ok(response: Response, what: string) {
  if (!response.ok) throw new Error(`${what}: ${response.status}`);
  return response;
}

/** Imports and saves each file's routes and tracks, filed in `folderGuid` (null = top level). */
export async function importToLibrary(files: File[], folderGuid: string | null): Promise<SavedItem[]> {
  const saved: SavedItem[] = [];
  for (const file of files) {
    const query = folderGuid ? `?folderGuid=${folderGuid}` : '';
    const response = await ok(
      await apiFetch(`/library/import${query}`, { method: 'POST', body: upload(file) }),
      'Import',
    );
    saved.push(...((await response.json()) as SavedItem[]));
  }
  return saved;
}

/** Reads each file without saving; its routes and tracks become unsaved trails to open on the map. */
export async function readForMap(files: File[]): Promise<TrailRef[]> {
  const refs: TrailRef[] = [];
  for (const file of files) {
    const importType = /\.tcx$/i.test(file.name) ? 2 : 1;
    const response = await ok(
      await apiFetch(`/data/importRoute?importType=${importType}&DoSave=false&version=1`, {
        method: 'POST',
        body: upload(file),
      }),
      'Read file',
    );
    const container = (await response.json()) as { routes?: ApiTrail[]; tracks?: ApiTrail[] };
    for (const route of container.routes ?? []) refs.push({ kind: 'route', guid: addDraft('route', route) });
    for (const track of container.tracks ?? []) refs.push({ kind: 'track', guid: addDraft('track', track) });
  }
  return refs;
}

/** Saves a new route or track into a folder (null = top level). */
export async function saveToLibrary(
  kind: TrailKind,
  trail: ApiTrail,
  folderGuid: string | null,
): Promise<SavedItem> {
  const response = await ok(
    await apiFetch('/library/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // A new trail has no id yet: leave it out (the server reads "" as an invalid Guid) and it gets one.
      body: JSON.stringify({ kind, trail: { ...trail, guid: trail.guid || undefined }, folderGuid }),
    }),
    'Save',
  );
  return (await response.json()) as SavedItem;
}
